import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Users } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import ProcedureSelect from './ProcedureSelect';
import { ENGAGE_SOURCE_LABEL } from '../../utils/engageStageBadge';
import { SOURCE_LINK_CONFIG, ENGAGE_SOURCE_OPTIONS } from '../../utils/engageSourceLinks';


const SOURCE_OPTIONS = ENGAGE_SOURCE_OPTIONS;

const DEPARTMENTS = [
  'General Physician', 'Cardiology', 'Orthopedics', 'Gynecology',
  'Pediatrics', 'Diabetology', 'Ophthalmology', 'ENT', 'Dermatology',
  'Neurology', 'Urology', 'Nephrology', 'Oncology', 'Psychiatry',
  'Preventive Health', 'Emergency', 'Other',
];


export default function NewEnquiry() {
  const navigate = useNavigate();
  const { create: apiCreate, fetch: apiFetch } = useApiMethods();

  const [form, setForm] = useState({
    name: '',
    mobile: '',
    email: '',
    source: 'phone',
    department_of_interest: '',
    doctor_of_interest: '',
    procedure_of_interest: '',
    expected_treatment_value: '',
    message: '',
    status: 'new',
    preferred_language: 'en',
    patient_type: '',
    stage: '',
  });

  const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'Hindi' },
    { value: 'ta', label: 'Tamil' },
    { value: 'ml', label: 'Malayalam' },
    { value: 'te', label: 'Telugu' },
  ];
  const [linkedEntityId, setLinkedEntityId] = useState('');
  const [linkOptions, setLinkOptions] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Patient type drives which stages are offered — the stage list is never a
  // static array, it is always the selected type's own ladder.
  const [patientTypes, setPatientTypes] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const linkCfg = SOURCE_LINK_CONFIG[form.source];

  // Fetch source-specific option list when source changes
  useEffect(() => {
    setLinkedEntityId('');
    setLinkOptions([]);
    if (!linkCfg) return;
    let cancelled = false;
    setLinkLoading(true);
    apiFetch(linkCfg.endpoint).then(res => {
      if (cancelled) return;
      const rows = res?.results || res?.data || [];
      const opts = (Array.isArray(rows) ? rows : []).map(linkCfg.mapOption);
      setLinkOptions(opts);
    }).catch(() => {
      if (!cancelled) setLinkOptions([]);
    }).finally(() => { if (!cancelled) setLinkLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.source]);

  // Load selectable patient types once.
  useEffect(() => {
    let cancelled = false;
    apiFetch('engage/patient-types/').then(res => {
      if (cancelled) return;
      const rows = res?.results || res?.data?.results || [];
      setPatientTypes(Array.isArray(rows) ? rows : []);
    }).catch(() => { if (!cancelled) setPatientTypes([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the type changes, pull that type's ladder and pre-select its entry
  // stage. Clearing the type clears the stage too — an orphan stage would be
  // rejected by the API anyway.
  useEffect(() => {
    setStageOptions([]);
    set('stage', '');
    if (!form.patient_type) return;
    let cancelled = false;
    setStagesLoading(true);
    apiFetch(`engage/patient-types/${form.patient_type}/stages/`).then(res => {
      if (cancelled) return;
      const rows = res?.results || res?.data?.results || [];
      const list = Array.isArray(rows) ? rows : [];
      setStageOptions(list);
      const entry = list.find(s => s.is_default) || list[0];
      if (entry) set('stage', String(entry.id));
    }).catch(() => { if (!cancelled) setStageOptions([]); })
      .finally(() => { if (!cancelled) setStagesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.patient_type]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      // Blank value → null so DRF treats it as "no quote entered".
      if (payload.expected_treatment_value === '' || payload.expected_treatment_value == null) {
        payload.expected_treatment_value = null;
      } else {
        payload.expected_treatment_value = Number(payload.expected_treatment_value);
      }
      if (linkCfg && linkedEntityId) {
        payload.source_details = { [linkCfg.detailsKey]: Number(linkedEntityId) };
      }
      // Send the FKs as ids, or omit them entirely — '' would fail DRF's
      // PrimaryKeyRelatedField validation.
      if (payload.patient_type) payload.patient_type = Number(payload.patient_type);
      else delete payload.patient_type;
      if (payload.stage) {
        payload.stage = Number(payload.stage);
        // stage wins; the API derives status from it.
        delete payload.status;
      } else {
        delete payload.stage;
      }
      const resp = await apiCreate('/engage/enquiries/', payload);
      const created = resp?.data || resp?.data?.data;
      if (created?.id) {
        navigate(`/engage/enquiries/${created.id}`);
      } else {
        navigate('/engage/enquiries');
      }
    } catch (err) {
      const msg = err?.response?.data?.message
                || (err?.response?.data && JSON.stringify(err.response.data))
                || 'Failed to create enquiry';
      setError(msg);
      setSaving(false);
    }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate('/engage/enquiries')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Enquiries
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
          <Users className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">New Patient Enquiry</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Capture an incoming lead</p>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Patient name <span className="text-red-500">*</span></label>
            <input required value={form.name} onChange={e => set('name', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mobile <span className="text-red-500">*</span></label>
            <input required value={form.mobile} onChange={e => set('mobile', e.target.value)}
              placeholder="+91…"
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Source <span className="text-red-500">*</span></label>
            <select required value={form.source} onChange={e => set('source', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              {SOURCE_OPTIONS.map(s => (
                <option key={s} value={s}>{ENGAGE_SOURCE_LABEL[s] || s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Department of interest</label>
            <select value={form.department_of_interest} onChange={e => set('department_of_interest', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">— Select —</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Preferred language <span className="text-gray-400 font-normal">(AI Voice Agent will open in this language)</span>
          </label>
          <select value={form.preferred_language} onChange={e => set('preferred_language', e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {LANGUAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Patient type → stage. The stage list is always the chosen type's own
            ladder, so the two can never disagree. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Patient type
            </label>
            <select value={form.patient_type} onChange={e => set('patient_type', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">— Not set —</option>
              {patientTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stage{' '}
              <span className="text-gray-400 font-normal">
                {form.patient_type
                  ? `(${stageOptions.length} for this type)`
                  : '(pick a patient type first)'}
              </span>
            </label>
            <select value={form.stage} onChange={e => set('stage', e.target.value)}
              disabled={!form.patient_type || stagesLoading}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">
              {stagesLoading ? (
                <option value="">Loading…</option>
              ) : stageOptions.length === 0 ? (
                <option value="">— None —</option>
              ) : (
                stageOptions.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.is_default ? ' (entry)' : ''}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Source-linked dropdown (appears only for referral-driven sources) */}
        {linkCfg && (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              {linkCfg.label}{' '}
              {linkCfg.optional
                ? <span className="text-gray-400 font-normal">(optional)</span>
                : <span className="text-red-500">*</span>}
            </label>
            <select required={!linkCfg.optional} value={linkedEntityId} onChange={e => setLinkedEntityId(e.target.value)}
              disabled={linkLoading}
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">
              <option value="">
                {linkLoading ? 'Loading…' : linkOptions.length === 0 ? `— No ${linkCfg.label.toLowerCase()} available —` : `— Select ${linkCfg.label.toLowerCase()} —`}
              </option>
              {linkOptions.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            {linkOptions.length === 0 && !linkLoading && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                No entries yet — add one via the sidebar first.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Doctor of interest</label>
            <input value={form.doctor_of_interest} onChange={e => set('doctor_of_interest', e.target.value)}
              placeholder="Dr. Ramesh Kumar (if any)"
              className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Procedure of interest</label>
            <ProcedureSelect
              value={form.procedure_of_interest}
              onChange={(v) => set('procedure_of_interest', v)}
              onPickCatalog={(row) => {
                if (!form.expected_treatment_value) {
                  set('expected_treatment_value', String(row.default_value || ''));
                }
                if (!form.department_of_interest && row.department) {
                  set('department_of_interest', row.department);
                }
              }}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Expected treatment value <span className="text-gray-400 font-normal">(optional — used in revenue projection)</span>
          </label>
          <div className="relative max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
            <input type="number" min="0" step="500"
              value={form.expected_treatment_value}
              onChange={e => set('expected_treatment_value', e.target.value)}
              placeholder="e.g. 175000 for angioplasty"
              className="w-full pl-7 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            Enter the quoted amount / package price. Blank = the dashboard will use department average.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Message / notes</label>
          <textarea rows={4} value={form.message} onChange={e => set('message', e.target.value)}
            placeholder="Free-text description of what the patient is asking about…"
            className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate('/engage/enquiries')}
            className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button type="submit"
            disabled={saving || !form.name.trim() || !form.mobile.trim() || (linkCfg && !linkedEntityId)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Creating…' : 'Create Enquiry'}
          </button>
        </div>
      </form>
    </div>
  );
}
